import { Checkbox } from '@/components/ui/checkbox';
import { formatCompact } from '@/lib/money';
import { tone, useIsDarkMode } from '@/lib/chartTheme';
import type { EngagementSeriesDatum } from '@/components/charts/EngagementChart';

/**
 * Engagement breakdown rows (real counts, summed from the same /statistics/engagements series the
 * chart plots) — port of CMP `EngagementBreakdownRowView` (checkable row + progress bar). The
 * checkbox toggles the matching chart line via `onToggle`, mirroring CMP's
 * `SetChartLineVisible` intents.
 */
export function EngagementBreakdown({
  series,
  hiddenSeries,
  onToggle,
}: {
  series: EngagementSeriesDatum[];
  hiddenSeries: Record<string, boolean>;
  onToggle: (name: string) => void;
}) {
  const isDark = useIsDarkMode();
  if (series.length === 0) return null;

  const max = Math.max(1, ...series.map((s) => s.total));

  return (
    <div className="flex flex-col gap-3">
      {series.map((s) => {
        const color = tone(s.color, isDark);
        const visible = !hiddenSeries[s.name];
        const pct = Math.round((s.total / max) * 100);
        return (
          <div key={s.name} className="flex flex-col gap-1.5">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={visible}
                onCheckedChange={() => onToggle(s.name)}
                aria-label={`Toggle ${s.name}`}
              />
              <span
                className="size-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: color }}
                aria-hidden
              />
              <span className="flex-1 text-sm text-muted-foreground">{s.name}</span>
              <span className="text-sm font-medium tabular-nums">{formatCompact(s.total)}</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${visible ? pct : 0}%`, backgroundColor: color }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
